import React, { useState, useCallback, lazy, Suspense, useEffect } from 'react';
import { checkSubscription, getAnnouncement } from '../services/api';

// 延迟加载公告弹窗组件 (Code Splitting)
const AnnouncementModal = lazy(() => import('./AnnouncementModal'));

const SubscriptionManager = () => {
  const [email, setEmail] = useState('');
  const [result, setResult] = useState(null);
  const [subscriptionLink, setSubscriptionLink] = useState('');
  // const [activationLinks, setActivationLinks] = useState({ singbox: '', clash: '' }); // Deprecated
  const [copiedState, setCopiedState] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [showAnnouncement, setShowAnnouncement] = useState(false);
  const [marqueeText, setMarqueeText] = useState('');
  const [trafficData, setTrafficData] = useState(null);

  useEffect(() => {
    getAnnouncement().then(data => {
      if (data && data.text) {
        setMarqueeText(data.text);
      }
    }).catch(console.error);
  }, []);

  const validateEmail = (email) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(String(email).toLowerCase());
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    setSubscriptionLink('');
    // setActivationLinks({ singbox: '', clash: '' });
    setCopiedState(false);
    setIsExpired(false);
    setTrafficData(null);
    if (!validateEmail(email)) {
      setError('请输入有效的邮箱地址');
      return;
    }
    setIsLoading(true);
    try {
      const subscriptionInfo = await checkSubscription(email);
      if (subscriptionInfo) {
        setResult(`订阅到期时间: ${subscriptionInfo.expirationDate}`);
        setIsExpired(!subscriptionInfo.isActive);
        setSubscriptionLink(subscriptionInfo.subscriptionLink || '');
        setTrafficData(subscriptionInfo.trafficData || null);
        // 移除 deprecated 的 activationLinks处理
      } else {
        setResult('未找到该邮箱的订阅信息');
      }
    } catch (error) {
      setError('查询出错，请稍后再试');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  }, [email]);

  // Deprecated import handlers removed
  const copySubscriptionLink = useCallback(() => {
    navigator.clipboard.writeText(subscriptionLink).then(
      () => {
        setCopiedState(true);
        setTimeout(() => setCopiedState(false), 3000);
      },
      () => setError('复制失败，请手动复制')
    );
  }, [subscriptionLink]);

  const handleRenewSubscription = useCallback(() => {
    // 这里替换为实际的订阅续费链接
    window.location.href = 'https://ouvg.top/buy/45';
  }, []);

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* 2026 Header - Animated & Centered */}
      <div className="relative z-10 text-center mb-10 pt-4 animate-float">
        <h1 className="text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-violet-700 via-indigo-700 to-slate-800 mb-2 drop-shadow-sm tracking-tight">
          麦门订阅
        </h1>
        <p className="text-lg text-slate-600 font-medium">全网节点 · 极速连接 · 4K秒开</p>
      </div>

      {/* Main Layout - Mobile First Stack */}
      <div className="relative z-10 max-w-7xl mx-auto flex flex-col lg:grid lg:grid-cols-3 gap-6 pb-20">

        {/* Hero Card: 订阅查询 (Mobile: Order 1, Desktop: Order 2) */}
        <div className="order-1 lg:order-2 animate-slide-up flex flex-col gap-4" style={{ animationDelay: '0.1s' }}>
          <div className="glass rounded-2xl p-6 shadow-2xl ring-1 ring-white/20 relative overflow-hidden group">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 opacity-50"></div>

            <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center justify-center">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600">
                订阅查询
              </span>
            </h2>

            {/* Quick Actions Grid */}
            <div className="grid grid-cols-3 gap-3 mb-8">
              <a href="https://ouvg.top/buy/45" target="_blank" rel="noopener noreferrer"
                className="flex flex-col items-center justify-center p-3 rounded-xl bg-indigo-50/50 hover:bg-indigo-100/80 transition-all cursor-pointer border border-indigo-100/50 hover:scale-105 active:scale-95">
                <span className="text-2xl mb-1">💎</span>
                <span className="text-xs font-medium text-indigo-700">购买</span>
              </a>
              <button onClick={() => setShowAnnouncement(true)}
                className="flex flex-col items-center justify-center p-3 rounded-xl bg-pink-50/50 hover:bg-pink-100/80 transition-all cursor-pointer border border-pink-100/50 hover:scale-105 active:scale-95">
                <span className="text-2xl mb-1">📢</span>
                <span className="text-xs font-medium text-pink-700">公告</span>
              </button>
              <a href="https://air.185101.xyz/" target="_blank" rel="noopener noreferrer"
                className="flex flex-col items-center justify-center p-3 rounded-xl bg-emerald-50/50 hover:bg-emerald-100/80 transition-all cursor-pointer border border-emerald-100/50 hover:scale-105 active:scale-95">
                <span className="text-2xl mb-1">📖</span>
                <span className="text-xs font-medium text-emerald-700">教程</span>
              </a>
            </div>

            {/* Floating Label Input Form */}
            <form onSubmit={handleSubmit} className="space-y-6 relative">
              <div className="relative group">
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="peer w-full px-4 py-4 bg-white/50 border-2 border-transparent rounded-xl outline-none focus:bg-white focus:ring-4 focus:ring-purple-500/20 focus:border-purple-500 transition-all duration-300 placeholder-transparent text-gray-800 font-medium"
                  placeholder="name@example.com"
                />
                <label htmlFor="email"
                  className="absolute left-4 -top-2.5 bg-white/80 backdrop-blur-sm px-2 text-xs font-bold text-purple-600 rounded-full transition-all 
                             peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-4 peer-placeholder-shown:bg-transparent 
                             peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-purple-600 peer-focus:bg-white/90">
                  输入您的邮箱地址
                </label>
              </div>

              <button
                type="submit"
                disabled={isLoading || !email}
                className={`group w-full py-4 rounded-xl font-bold text-lg tracking-wide transition-all shadow-lg hover:shadow-purple-500/30 overflow-hidden relative ${isLoading || !email
                  ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                  : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:scale-[1.02] active:scale-[0.98]'
                  }`}
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      查询中...
                    </>
                  ) : '立即查询'}
                </span>
                {/* Button Glow Effect */}
                {!isLoading && email && <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out"></div>}
              </button>
            </form>

            {/* 2026 Style Result Card */}
            <div className="mt-6">
              <div className={`p-5 rounded-xl border ${isExpired ? 'bg-red-50/80 border-red-200' : 'bg-green-50/80 border-green-200'} backdrop-blur-sm transition-all duration-500 animate-fade-in ${result ? 'block' : 'hidden'}`}>
                {result && (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-gray-500">到期时间</span>
                      {isExpired && <span className="text-xs font-bold bg-red-200 text-red-800 px-2 py-0.5 rounded-full">已过期</span>}
                      {!isExpired && <span className="text-xs font-bold bg-green-200 text-green-800 px-2 py-0.5 rounded-full">生效中</span>}
                    </div>
                    <p className="text-center text-xl text-gray-800 font-mono font-bold tracking-tight py-2">{result.replace('订阅到期时间: ', '')}</p>
                    {trafficData && (
                      <div className="mt-4 rounded-xl bg-white/70 border border-white/70 p-4 text-left shadow-sm">
                        <div className="flex items-center justify-between gap-3 mb-3">
                          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">剩余流量</span>
                          <span className="text-lg font-black text-indigo-700">{trafficData.remainingTraffic}</span>
                        </div>
                        <div className="h-2 rounded-full bg-slate-200 overflow-hidden" aria-hidden="true">
                          <div className="h-full w-full rounded-full bg-gradient-to-r from-emerald-400 via-sky-500 to-indigo-500"></div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg animate-pulse">
                  {error}
                </div>
              )}

              {/* 2026 Copy Button */}
              {!isExpired && subscriptionLink && (
                <div className="mt-4">
                  <button
                    onClick={copySubscriptionLink}
                    disabled={copiedState}
                    className={`group w-full py-3 px-4 rounded-xl font-bold flex items-center justify-center gap-2 transition-all duration-200 ${copiedState
                      ? 'bg-green-500 text-white shadow-none scale-95'
                      : 'bg-white text-indigo-600 border-2 border-indigo-100 hover:border-indigo-200 hover:bg-indigo-50 shadow-sm hover:shadow-md'
                      }`}
                  >
                    {copiedState ? (
                      <>
                        <span className="text-lg">✅</span>
                        <span>已复制到剪贴板</span>
                      </>
                    ) : (
                      <>
                        <span className="text-lg group-hover:rotate-12 transition-transform">📋</span>
                        <span>复制订阅链接</span>
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* 2026 Renew Button - Pulsing */}
              {isExpired && result && (
                <button
                  onClick={handleRenewSubscription}
                  className="mt-4 w-full py-4 px-4 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold rounded-xl transition duration-300 shadow-lg hover:shadow-orange-500/40 relative overflow-hidden animate-pulse-slow"
                >
                  😭 立即续订复活
                </button>
              )}
            </div>
          </div>

          {/* Marquee Announcement */}
          {marqueeText && (
            <div className="glass rounded-xl px-4 py-3 shadow-lg ring-1 ring-white/20 relative flex items-center gap-3 overflow-hidden bg-white/40 border-t border-white/50 animate-fade-in">
              <span className="text-lg shrink-0 z-10 w-8 h-8 flex items-center justify-center bg-white/50 rounded-full shadow-sm">📢</span>
              <div className="marquee-mask flex-1 overflow-hidden relative" style={{ height: '24px' }}>
                <div className="marquee-content absolute text-sm font-bold text-slate-700 tracking-wide">
                  {marqueeText}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Status Card (Mobile: Order 2, Desktop: Order 1) */}
        <div className="order-2 lg:order-1 glass rounded-2xl p-6 shadow-xl relative overflow-hidden animate-slide-up" style={{ animationDelay: '0s' }}>
          <div className="absolute top-0 right-0 w-20 h-20 bg-emerald-400/20 rounded-full blur-2xl -mr-10 -mt-10"></div>

          <div className="flex items-center mb-6 relative z-10">
            <div className="w-3 h-3 bg-emerald-500 rounded-full mr-3 shadow-[0_0_12px_rgba(16,185,129,0.6)] animate-pulse"></div>
            <h2 className="text-xl font-bold text-slate-800">节点状态</h2>
          </div>

          <div className="grid grid-cols-2 gap-3 relative z-10">
            <div className="col-span-2 p-3 bg-white/60 rounded-xl flex justify-between items-center border border-white/50 shadow-sm">
              <span className="text-sm font-semibold text-slate-700">订阅API</span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                <span className="text-xs font-bold text-emerald-600">正常</span>
              </span>
            </div>

            {[
              { name: '香港', tag: 'HK' },
              { name: '台湾', tag: 'TW' },
              { name: '日本', tag: 'JP' },
              { name: '新加坡', tag: 'SG' },
              { name: '韩国', tag: 'KR' },
              { name: '美国', tag: 'US' }
            ].map((region) => (
              <div key={region.tag} className="p-3 bg-white/40 hover:bg-white/70 rounded-xl border border-white/40 transition-all hover:scale-105 cursor-default flex flex-col items-center justify-center gap-1">
                <span className="text-xs font-bold text-slate-500">{region.tag}</span>
                <span className="text-sm font-bold text-slate-800">{region.name}</span>
                <span className="w-8 h-1 bg-emerald-400/50 rounded-full mt-1"></span>
              </div>
            ))}
          </div>
        </div>

        {/* Tools Card (Mobile: Order 3, Desktop: Order 3) */}
        <div className="order-3 glass rounded-2xl p-6 shadow-xl relative overflow-hidden animate-slide-up" style={{ animationDelay: '0.2s' }}>
          <div className="absolute bottom-0 right-0 w-32 h-32 bg-indigo-400/10 rounded-full blur-3xl -mr-10 -mb-10"></div>

          <h2 className="text-xl font-bold text-slate-800 mb-6 relative z-10">实用工具</h2>
          <div className="grid grid-cols-1 gap-4 relative z-10">

            <a href="https://air.185101.xyz/" target="_blank" rel="noopener noreferrer"
              className="group bg-gradient-to-br from-blue-50 to-blue-100/50 p-4 rounded-xl border border-blue-100/50 hover:shadow-lg hover:shadow-blue-500/10 transition-all hover:-translate-y-1 flex items-center">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-2xl mr-4 group-hover:scale-110 transition-transform">
                📖
              </div>
              <div>
                <h3 className="font-bold text-slate-800">使用教程</h3>
                <p className="text-xs text-slate-500 mt-1">iOS / Android / PC 配置指南</p>
              </div>
            </a>

            <a href="https://pan.185101.xyz" target="_blank" rel="noopener noreferrer"
              className="group bg-gradient-to-br from-emerald-50 to-emerald-100/50 p-4 rounded-xl border border-emerald-100/50 hover:shadow-lg hover:shadow-emerald-500/10 transition-all hover:-translate-y-1 flex items-center">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-2xl mr-4 group-hover:scale-110 transition-transform">
                🎬
              </div>
              <div>
                <h3 className="font-bold text-slate-800">影视资源</h3>
                <p className="text-xs text-slate-500 mt-1">麦麦超全网盘影视搜索</p>
              </div>
            </a>

            <a href="https://so.185101.xyz" target="_blank" rel="noopener noreferrer"
              className="group bg-gradient-to-br from-purple-50 to-purple-100/50 p-4 rounded-xl border border-purple-100/50 hover:shadow-lg hover:shadow-purple-500/10 transition-all hover:-translate-y-1 flex items-center">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-2xl mr-4 group-hover:scale-110 transition-transform">
                🔗
              </div>
              <div>
                <h3 className="font-bold text-slate-800">实用网址</h3>
                <p className="text-xs text-slate-500 mt-1">资源工具集合</p>
              </div>
            </a>

          </div>
        </div>
      </div>

      {/* 公告弹窗 - Lazy Loaded */}
      {showAnnouncement && (
        <Suspense fallback={
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="bg-white/90 glass rounded-2xl p-8 shadow-2xl">
              <div className="animate-spin h-8 w-8 border-4 border-indigo-600 border-t-transparent rounded-full mx-auto"></div>
            </div>
          </div>
        }>
          <AnnouncementModal onClose={() => setShowAnnouncement(false)} />
        </Suspense>
      )}
    </div>
  );
};

export default SubscriptionManager;
